import { type ReactNode, useMemo } from 'react';
import { RadioGroup, CheckboxGroup } from '@/components/molecules';
import { CollapseGroup } from '@/components/organisms/CollapseGroup/CollapseGroup';
import { useFilters, type FilterValue } from '@/contexts/FiltersContext.context';
import { useFilteredPlansCount } from '@/contexts/FilteredPlansContext.context';
import { OfferCountBadge } from '@/components/atoms';
import type { RadioOption } from '@/components/molecules/RadioGroup/RadioGroup';
import type { CheckboxOption } from '@/components/molecules/CheckboxGroup/CheckboxGroup';
import { PLAN_TYPES, type PlanType } from '@/config/api';
import { getFiltersConfig, type LegacyFilterConfig } from '@/config/filters';
import resetIcon from '@/assets/icons/round-circle-arrow.svg';
import './FilterGroup.scss';

export interface FilterOption {
  value: string;
  label: string;
  responsiveLabel?: string;
  icon?: string;
  image?: string;
}

export interface FilterItem {
  name: string;
  type: 'radio' | 'checkbox';
  label?: string;
  options: FilterOption[];
  defaultValue?: FilterValue;
  variant?: 'icon' | 'text';
}

export interface CollapseFilterItem {
  type: 'collapse';
  label: string;
  defaultOpen?: boolean;
  children: FilterItem[];
}

export type FilterConfig = FilterItem | CollapseFilterItem;

export interface FilterGroupProps {
  id?: string;
  title?: string;
  planType?: PlanType;
  filters?: FilterConfig[];
  children?: ReactNode;
  showReset?: boolean;
  initialFilters?: Record<string, FilterValue>;
}

/**
 * Converts legacy filter configs to FilterConfig[] for auto-rendering.
 */
function legacyToFilterConfigs(legacyFilters: LegacyFilterConfig[]): FilterConfig[] {
  return legacyFilters.map((legacy) => {
    if (legacy.type === 'collapse-checkbox') {
      return {
        type: 'collapse' as const,
        label: legacy.label,
        defaultOpen: false,
        children: [{
          name: legacy.name,
          type: 'checkbox' as const,
          options: legacy.options as FilterOption[],
          defaultValue: legacy.defaultValue,
        }],
      };
    }
    return {
      name: legacy.name,
      type: legacy.type as 'radio' | 'checkbox',
      label: legacy.label,
      options: legacy.options as FilterOption[],
      defaultValue: legacy.defaultValue,
      variant: legacy.type === 'checkbox' ? 'icon' as const : undefined,
    };
  });
}

interface FilterItemRendererProps {
  config: FilterConfig;
}

function FilterItemRenderer({ config }: FilterItemRendererProps) {
  if (config.type === 'collapse') {
    return (
      <CollapseGroup label={config.label} defaultOpen={config.defaultOpen}>
        {config.children.map((child) => (
          <FilterItemRenderer key={child.name} config={child} />
        ))}
      </CollapseGroup>
    );
  }

  if (config.type === 'radio') {
    return (
      <RadioGroup
        name={config.name}
        label={config.label}
        options={config.options as RadioOption[]}
      />
    );
  }

  if (config.type === 'checkbox') {
    return (
      <CheckboxGroup
        name={config.name}
        label={config.label}
        variant={config.variant}
        options={config.options as CheckboxOption[]}
      />
    );
  }

  return null;
}

export function FilterGroup({ title, planType, filters, children, showReset = true }: FilterGroupProps) {
  const { resetFilters } = useFilters();
  const offerCount = useFilteredPlansCount();

  // Auto-generate filters from planType when no children or filters are provided
  const autoFilters = useMemo(() => {
    if (children || filters) return null;
    if (!planType) return null;
    const filterType = planType === PLAN_TYPES.INTERNET ? 'internet' : 'mobile';
    return legacyToFilterConfigs(getFiltersConfig(filterType));
  }, [planType, children, filters]);

  const effectiveFilters = filters ?? autoFilters ?? undefined;

  return (
    <div className="filter-group bg-background-page">
      {title && (
        <div className="filter-group__header">
          <div className="filter-group__header-top">
            <div className="filter-group__header-title">
              {title}
              <OfferCountBadge count={offerCount} />
            </div>
            {showReset && (
              <button className="filter-group__reset" onClick={resetFilters} type="button">
                Réinitialiser
                <img src={resetIcon} alt="" width={14} height={14} />
              </button>
            )}
          </div>
          <hr />
        </div>
      )}
      {children}
      {!children && effectiveFilters?.map((config) => (
        <FilterItemRenderer
          key={config.type === 'collapse' ? config.label : (config as FilterItem).name}
          config={config}
        />
      ))}
    </div>
  );
}

export default FilterGroup;
